import { addDays, format, isAfter, startOfDay } from 'date-fns';
import { DataSheetResult } from '../insights-data.interface';

export class EnterDataSaveNextUtility {
  /**
   * Check what types of data exist for a given date in the behavior data.
   * Used to determine which "Continue With..." buttons to show.
   */
  static getDataForDate(
    behaviorData: DataSheetResult | undefined,
    dateToCheck: string
  ): { hasDuration: boolean; hasFrequency: boolean } {
    const hasFrequencyOnly =
      behaviorData?.activityDataUnitValues?.some(
        record => record.dataDate === dateToCheck
      ) ?? false;

    const hasDurationData =
      behaviorData?.activityDataUnitMultiValues?.some(
        record =>
          record.dataDate === dateToCheck &&
          (record.isDurationValue ?? record.isDuration) === true
      ) ?? false;

    const hasIntervalFrequency =
      behaviorData?.activityDataUnitMultiValues?.some(
        record =>
          record.dataDate === dateToCheck &&
          (record.isDurationValue ?? record.isDuration) === false
      ) ?? false;

    return {
      hasDuration: hasDurationData && !hasFrequencyOnly,
      hasFrequency: hasFrequencyOnly || hasIntervalFrequency,
    };
  }

  /**
   * Returns hasDuration / hasFrequency / hasNoData flags for the next working date.
   * All three components use this to decide which "Continue With..." buttons to show.
   */
  static getNextDateInfo(
    behaviorData: DataSheetResult | undefined,
    selectedDate: string
  ): { hasDuration: boolean; hasFrequency: boolean; hasNoData: boolean } {
    const nextDate = this.getNextWorkingDate(selectedDate);
    if (!nextDate) {
      return { hasDuration: false, hasFrequency: false, hasNoData: false };
    }
    const data = this.getDataForDate(behaviorData, nextDate);
    return { ...data, hasNoData: !data.hasDuration && !data.hasFrequency };
  }

  private static parseDateInput(dateInput: string | Date): Date {
    if (dateInput instanceof Date) {
      return startOfDay(dateInput);
    }

    // Parse YYYY-MM-DD as local date to avoid UTC timezone shifts
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    return startOfDay(new Date(dateInput));
  }

  /**
   * Get the next working date after the given date (excludes weekends)
   * Only returns a date if it's <= today
   * @param selectedDate Current selected date (YYYY-MM-DD format)
   * @returns Next working date string (YYYY-MM-DD) or null if no valid next date
   */
  static getNextWorkingDate(selectedDate: string): string | null {
    if (!selectedDate) return null;
    const date = this.parseDateInput(selectedDate);
    const today = startOfDay(new Date());
    let next = startOfDay(addDays(date, 1));

    // Skip weekends (0 = Sunday, 6 = Saturday)
    while (next.getDay() === 0 || next.getDay() === 6) {
      next = startOfDay(addDays(next, 1));
    }

    // Only return if next date is <= today
    if (isAfter(startOfDay(next), today)) return null;
    return format(next, 'yyyy-MM-dd');
  }

  /**
   * Determine if "Save & Next" button should be shown
   * @param selectedUnit The selected data unit (hide for unit 1)
   * @param selectedDate Current selected date
   * @returns true if Save & Next button should be visible
   */
  static shouldShowSaveAndNext(
    selectedUnit: number,
    selectedDate: string
  ): boolean {
    // Hide for selectedUnit === 1 (Set Frequency / Set Duration scenario)
    if (selectedUnit === 1) return false;
    // Hide if no next working date available
    return this.getNextWorkingDate(selectedDate) !== null;
  }

  /**
   * Get the day name for a date string
   * @param dateString Date string or Date object
   * @returns Day name (e.g., 'Monday')
   */
  static getDayName(dateString: string | Date): string {
    const date = this.parseDateInput(dateString);
    return format(date, 'EEEE');
  }

  /**
   * Format a date string to YYYY-MM-DD format
   * @param dateString Date string or Date object
   * @returns Formatted date string
   */
  static formatDate(dateString: string | Date): string {
    const date = this.parseDateInput(dateString);
    return format(date, 'yyyy-MM-dd');
  }
}
